import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import FlowCard from './FlowCard';

// 24-hour, no seconds (LV-friendly): "13.08.2026 09:30"
function formatMeeting(iso) {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * confirmed / meeting_proposed: negotiate a meeting time.
 * Roles are made explicit so a counter-proposal can never accidentally advance:
 *  - the proposer never sees "Accept" (only "waiting" + "suggest another time")
 *  - the non-proposer gets an explicit "Accept this time" (guarded not_proposer)
 * Advancing to meeting_agreed requires the non-proposer to Accept.
 */
export default function MeetingCard({ row = {}, actions = [], dispatch, busy, roleColorVar }) {
  const { t } = useTranslation('common');
  const [time, setTime] = useState(null);
  const [picking, setPicking] = useState(false);

  const proposal =
    row.meeting_time_proposal || row.pickup_meeting_proposal || row.delivery_meeting_proposal || null;
  const proposedTime = proposal && proposal.time;
  const canPropose = Boolean(actions.find((a) => a.action === 'propose_time'));
  const canAgree = Boolean(actions.find((a) => a.action === 'agree_time' && a.guardOk));
  const canCancel = Boolean(actions.find((a) => a.action === 'cancel'));
  const markDone = actions.find((a) => a.action === 'mark_done');
  const showPicker = picking || (!proposedTime && canPropose);

  const submitPropose = () => {
    if (!time) return;
    dispatch('propose_time', { meetingTime: time.toISOString() });
    setPicking(false);
    setTime(null);
  };

  return (
    <FlowCard step={t('flow.step_meeting')} title={t('flow.meeting_title')} roleColorVar={roleColorVar}>
      {proposedTime && !showPicker && (
        <>
          <p className="flow-proposed">{t('flow.proposed_time', { time: formatMeeting(proposedTime) })}</p>
          <p className="flow-proposed-hint">
            {canAgree ? t('flow.proposal_review_hint') : t('flow.proposal_waiting_hint')}
          </p>
        </>
      )}

      {showPicker && (
        <div className="flow-field">
          <DatePicker
            selected={time}
            onChange={setTime}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="dd.MM.yyyy HH:mm"
            minDate={new Date()}
            withPortal
            placeholderText={t('flow.pick_time')}
          />
        </div>
      )}

      <div className="flow-card-actions">
        {canAgree && !showPicker && (
          <button
            type="button"
            className="flow-btn flow-btn--primary"
            disabled={busy}
            data-action="agree_time"
            onClick={() => dispatch('agree_time')}
          >
            {t('flow.accept_time')}
          </button>
        )}

        {showPicker && (
          <button
            type="button"
            className="flow-btn flow-btn--primary"
            disabled={!time || busy}
            data-action="propose_time"
            onClick={submitPropose}
          >
            {t('flow.propose_time')}
          </button>
        )}

        {/* "Back" closes the picker — only meaningful when it was opened manually
            over an existing proposal (when it auto-opens with no proposal there's
            nothing to go back to, and Cancel below cancels the ride instead). */}
        {picking && proposedTime && (
          <button
            type="button"
            className="flow-btn flow-btn--secondary"
            disabled={busy}
            onClick={() => {
              setPicking(false);
              setTime(null);
            }}
          >
            {t('buttons.back')}
          </button>
        )}

        {!showPicker && canPropose && (
          <button
            type="button"
            className="flow-btn flow-btn--secondary"
            disabled={busy}
            data-action="open-picker"
            onClick={() => setPicking(true)}
          >
            {proposedTime ? t('flow.suggest_other_time') : t('flow.propose_time')}
          </button>
        )}

        {/* Cancel the ride/delivery — always available in this step. Was hidden
            whenever the picker was open, so it was unreachable during the initial
            "offer a time" step (the only visible Cancel just closed the picker). */}
        {canCancel && (
          <button
            type="button"
            className="flow-btn flow-btn--secondary"
            disabled={busy}
            data-action="cancel"
            onClick={() => dispatch('cancel')}
          >
            {t('flow.cancel')}
          </button>
        )}

        {markDone && !showPicker && (
          <button
            type="button"
            className="flow-btn flow-btn--secondary"
            disabled={!markDone.guardOk || busy}
            data-action="mark_done"
            onClick={() => dispatch('mark_done')}
          >
            {t('flow.mark_done')}
          </button>
        )}
      </div>
    </FlowCard>
  );
}
